import React, { useState } from 'react';
import {
  Paper,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  IconButton,
  Popover,
  Typography,
  Divider,
  Stack,
  ListSubheader,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import dayjs from 'dayjs';
import { users } from '../../../data/sampleData';
import { caseStages } from '../../../data/caseStages';
import { Filter } from './types';

interface CasesFiltersProps {
  searchQuery: string;
  statusFilter: string;
  practiceAreaFilter: string;
  practiceAreas: string[];
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onPracticeAreaChange: (value: string) => void;
  onClearFilters: () => void;
  advancedFilters?: Filter[];
  onAdvancedFilterAdd?: (filter: Filter) => void;
  onAdvancedFilterRemove?: (filterId: string) => void;
  advancedMenuAnchor: HTMLElement | null;
  onAdvancedMenuClose: () => void;
  onAdvancedClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export const CasesFilters: React.FC<CasesFiltersProps> = ({
  searchQuery,
  statusFilter,
  practiceAreaFilter,
  practiceAreas,
  onSearchChange,
  onStatusChange,
  onPracticeAreaChange,
  onClearFilters,
  advancedFilters = [],
  onAdvancedFilterAdd,
  onAdvancedFilterRemove,
  advancedMenuAnchor,
  onAdvancedMenuClose,
  onAdvancedClick,
}) => {
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const handleAdvancedFilterAdd = (type: Filter['type']) => {
    if (!onAdvancedFilterAdd) return;

    let filter: Filter | null = null;

    switch (type) {
      case 'stage':
        if (selectedStage) {
          const stage = caseStages.find(s => s.id === selectedStage);
          if (stage) {
            filter = {
              id: `stage-${Date.now()}`,
              type: 'stage',
              value: stage.id,
              label: `Stage: ${stage.name}`,
            };
          }
        }
        break;

      case 'staff':
        if (selectedStaff) {
          const staff = users.find(u => u.id === selectedStaff);
          if (staff) {
            filter = {
              id: `staff-${Date.now()}`,
              type: 'staff',
              value: staff.id,
              label: `Staff: ${staff.name}`,
            };
          }
        }
        break;

      case 'client':
        if (selectedClient) {
          const client = users.find(u => u.id === selectedClient);
          if (client) {
            filter = {
              id: `client-${Date.now()}`,
              type: 'client',
              value: client.id,
              label: `Client: ${client.name}`,
            };
          }
        }
        break;

      case 'date':
        if (selectedDate) {
          filter = {
            id: `date-${Date.now()}`,
            type: 'date',
            value: selectedDate.toISOString(),
            label: `Date: ${selectedDate.format('MMM D, YYYY')}`,
          };
        }
        break;
    }

    if (filter) {
      onAdvancedFilterAdd(filter);
      // Reset the corresponding field
      switch (type) {
        case 'stage':
          setSelectedStage('');
          break;
        case 'staff':
          setSelectedStaff('');
          break;
        case 'client':
          setSelectedClient('');
          break;
        case 'date':
          setSelectedDate(null);
          break;
      }
    }
  };

  // Group stages by category
  const stagesByCategory = caseStages.reduce((acc, stage) => {
    if (!acc[stage.category]) {
      acc[stage.category] = [];
    }
    acc[stage.category].push(stage);
    return acc;
  }, {} as Record<string, typeof caseStages>);

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search cases..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 200 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')}>
                  <X size={16} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => onStatusChange(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Practice Area</InputLabel>
          <Select
            value={practiceAreaFilter}
            label="Practice Area"
            onChange={(e) => onPracticeAreaChange(e.target.value)}
          >
            <MenuItem value="all">All Areas</MenuItem>
            {practiceAreas.map(area => (
              <MenuItem key={area} value={area}>{area}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SlidersHorizontal size={16} />}
          onClick={onAdvancedClick}
          color={advancedFilters.length > 0 ? 'primary' : 'inherit'}
          sx={{ height: 40 }}
        >
          Advanced
          {advancedFilters.length > 0 && (
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 20,
                textAlign: 'center',
              }}
            >
              {advancedFilters.length}
            </Box>
          )}
        </Button>
      </Box>

      {advancedFilters.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {advancedFilters.map((filter) => (
            <Chip
              key={filter.id}
              label={filter.label}
              size="small"
              onDelete={() => onAdvancedFilterRemove?.(filter.id)}
              color="primary"
              variant="outlined"
            />
          ))}
          <Button
            size="small"
            color="inherit"
            onClick={onClearFilters}
            sx={{ ml: 'auto' }}
          >
            Clear All
          </Button>
        </Box>
      )}

      <Popover
        open={Boolean(advancedMenuAnchor)}
        anchorEl={advancedMenuAnchor}
        onClose={onAdvancedMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { width: 320 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2">Advanced Filters</Typography>
            <IconButton
              size="small"
              onClick={onAdvancedMenuClose}
              sx={{ color: 'text.secondary' }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Filter by Stage
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Select Stage</MenuItem>
                  {Object.entries(stagesByCategory).map(([category, stages]) => [
                    <ListSubheader key={category}>{category}</ListSubheader>,
                    ...stages.map(stage => (
                      <MenuItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </MenuItem>
                    ))
                  ]).flat()}
                </Select>
              </FormControl>
              <Button
                size="small"
                disabled={!selectedStage}
                onClick={() => handleAdvancedFilterAdd('stage')}
                sx={{ mt: 1 }}
              >
                Add Stage Filter
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Filter by Staff
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedStaff}
                  onChange={(e) => setSelectedStaff(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Select Staff Member</MenuItem>
                  {users.filter(u => u.role !== 'client').map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                disabled={!selectedStaff}
                onClick={() => handleAdvancedFilterAdd('staff')}
                sx={{ mt: 1 }}
              >
                Add Staff Filter
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Filter by Client
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Select Client</MenuItem>
                  {users.filter(u => u.role === 'client').map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                disabled={!selectedClient}
                onClick={() => handleAdvancedFilterAdd('client')}
                sx={{ mt: 1 }}
              >
                Add Client Filter
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Filter by Date
              </Typography>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  }
                }}
              />
              <Button
                size="small"
                disabled={!selectedDate}
                onClick={() => handleAdvancedFilterAdd('date')}
                sx={{ mt: 1 }}
              >
                Add Date Filter
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </Paper>
  );
};